import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
  input,
  viewChild,
  output,
} from '@angular/core';
import { Apollo } from 'apollo-angular';
import { v4 as uuidv4 } from 'uuid';
import { EventBusService } from '@app/core/services/event-bus/event-bus.service';
import { Subject, zip, Subscription, timer } from 'rxjs';
import { FormatService } from '../ag1-chart/services/format.service';
import { AutoRefreshService } from '../ag1-chart/services/auto-refresh.service';
import {
  Filter,
  Report,
  ReportQuery,
  ReportGQL,
  ReportQueryVariables,
  Config,
  RunReportAsyncQueryVariables,
  RunReportAsyncQuery,
  RunReportAsyncGQL,
  FetchReportResultGQL,
  FetchReportResultQuery,
} from '@app/generated/graphql';
import { filter } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import * as Sentry from '@sentry/browser';
import {
  ReportConfig,
  kpiCustomConfig,
  Unit,
} from '@app/core/interfaces/report.interface';
import { PollingService } from '@app/core/services/polling/polling.service';
import { ActivatedRoute } from '@angular/router';
import { Ag1BettabaseErrorComponent } from '../ag1-bettabase-error/ag1-bettabase-error.component';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ag1-kpi',
  templateUrl: './ag1-kpi.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Ag1BettabaseErrorComponent, Ag1LoaderComponent],
})
export class Ag1KpiComponent implements OnInit, OnDestroy {
  private _destroyRef = inject(DestroyRef);
  private eventBus = inject(EventBusService);
  private reportGQL = inject(ReportGQL);
  private runReportAsyncGQL = inject(RunReportAsyncGQL);
  private fetchReportResultGQL = inject(FetchReportResultGQL);
  private apollo = inject(Apollo);
  private datePipe = inject(DatePipe);
  private formatService = inject(FormatService);
  private autoRefreshService = inject(AutoRefreshService);
  private pollingService = inject(PollingService);
  private route = inject(ActivatedRoute);

  readonly reportId = input<string>(undefined);
  readonly kpiContainer = viewChild<ElementRef>('kpiContainer');

  appliedReportGroupFilters: Filter[];
  reportConfig: ReportConfig;
  reportData: any;

  // state
  isLoading: boolean = true;
  isError: boolean = false;
  uuid: string;
  // refresh rate in milliseconds
  refreshRate: number | null = null;
  // switchUnits
  units: Unit[] = [];
  respond_to_switch_unit: boolean = false;

  // items to display
  periodType: string = 'period';
  valueToDisplay: string;
  previousValueToDisplay: string;
  periodToDisplay: string;
  percentChange: number;
  percentChangeToDisplay: string;
  title: string;
  subtitle: string;
  description: string;
  trendEnabled: boolean = false;

  // Destroy subject for managing subscription cleanup
  private destroy$ = new Subject<void>();

  // stores the subscription for the refresh interval
  private refreshSubscription: Subscription;
  private dataReceived = false; //tracks whether subscription ping on refetchReport has been received

  private readonly TIMEOUT_LIMIT = 30000; // in milliseconds
  private timeoutSubscription: Subscription;

  // error code
  errorName: string;
  // error message
  errorMessage: string;

  private pollingSubscription: Subscription;

  readonly loaded = output<string>();

  private reportGroupCycleId: number = null;

  constructor() {
    // Get cycle ID from query params
    const cycleParam = this.route.snapshot.queryParamMap.get(
      'report_group_cycle_id',
    );
    this.reportGroupCycleId = cycleParam ? parseInt(cycleParam, 10) : null;
  }

  ngOnInit(): void {
    this.eventBus
      .getChannel('appliedReportGroupFilters')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((filters) => Array.isArray(filters)),
      )
      .subscribe((appliedReportGroupFilters) => {
        this.isLoading = true;
        this.appliedReportGroupFilters = appliedReportGroupFilters;
        this.getChartAsync();
      });
    this.eventBus
      .getChannel('RefetchReport')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((data: { id: string; status: string }) => !!data?.id),
      )
      .subscribe((data: { id: string; status: string }) => {
        if (this.uuid === data.id) {
          if (data.status === 'failed') {
            const error = new Error(
              'Please refresh the page or check your connection. If the problem persists, contact support.',
            );
            error.name = 'Report Subscription Error';
            this.handleError(error);
          } else {
            this.refetchReport(data.id);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.refreshSubscription?.unsubscribe();
    this.pollingSubscription?.unsubscribe();
    this.timeoutSubscription?.unsubscribe();
  }

  handleError(error: Error, type?: string): void {
    if (!this.isError) {
      // Clear timeout subscription to prevent timeout state from overriding error state
      this.timeoutSubscription?.unsubscribe();
      console.error(error);

      // Handle error
      this.errorName = error?.name || 'An error occurred';
      this.errorMessage = error?.message;
      this.isLoading = false;
      this.isError = true; // slightly confusing naming here. This controlls rendering for both errors and normal communication

      this.loaded.emit(this.reportId());

      if (type !== 'communication') {
        Sentry.captureException(error);
      }
      throw error; // we still throw an 'error' for a communication to stop any further processing
    }
  }

  // clear error state and reset cahrt
  resetKpi(): void {
    this.errorName = null;
    this.errorMessage = null;
    this.isLoading = true;
    this.isError = false;
    this.dataReceived = false;
    this.pollingSubscription?.unsubscribe();
  }

  refetchReport(id: string): void {
    // Only proceed if this is our results
    if (this.uuid !== id) {
      return;
    }

    const reportVariables: ReportQueryVariables = {
      reportId: parseInt(this.reportId(), 10),
    };
    // reportGQL stream
    const configRef$ = this.apollo.query<ReportQuery>({
      query: this.reportGQL.document,
      variables: reportVariables,
      // config doesn't change too often
      fetchPolicy: 'cache-first',
    });

    // runReportGQL stream
    const dataRef$ = this.apollo.query<FetchReportResultQuery>({
      query: this.fetchReportResultGQL.document,
      variables: { requestUuid: id },
      // fetch latest
      fetchPolicy: 'no-cache',
    });

    zip(configRef$, dataRef$)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(
        ([configRes, dataRes]) => {
          try {
            const report: Report = configRes.data.report;
            const config: Config[] = structuredClone(report.configs);
            this.reportConfig = this.getReportConfig(config);
            this.title = this.reportConfig?.kpiCustomConfig?.title;

            const reportData = dataRes.data.fetchReportResult;
            this.dataReceived = reportData !== 'null'; // when the data was not in the redis cache on the backend .fetchReportResult is set to 'null'

            // set refreshRate if autoRefresh is enabled in kpiCustomConfig and we're not in cycle mode
            if (
              !this.refreshSubscription &&
              this.reportConfig?.kpiCustomConfig?.autoRefresh?.enabled &&
              !this.reportGroupCycleId
            ) {
              this.initializeAutoRefresh(this.reportConfig);
            }

            if (this.dataReceived) {
              // Clear timeout subscription if report data is received
              this.timeoutSubscription?.unsubscribe();

              const parsedReportData = JSON.parse(reportData);
              this.reportData = parsedReportData?.result;
              this.setChartOptions(this.reportConfig);
              if (this.respond_to_switch_unit) {
                this.subscribeToUnitChanges(); // want this to trigger after dataReceived
              }

              this.loaded.emit(this.reportId());
            }
          } catch (error) {
            this.handleError(error);
          }
        },
        (error) => {
          this.handleError(error);
        },
      );
  }

  getChartAsync(): void {
    try {
      this.resetKpi();

      this.uuid = uuidv4();

      // Start a timeout timer to display a timeout message if no data is received
      this.timeoutSubscription = timer(this.TIMEOUT_LIMIT)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(() => {
          if (!this.dataReceived) {
            const error = new Error(
              'Please refresh the page or check your connection. If the problem persists, contact support.',
            );
            error.name = 'Chart timed out';
            this.handleError(error, 'communication');
          }
        });

      const reportDataVariables: RunReportAsyncQueryVariables = {
        requestUuid: this.uuid,
        reportId: parseInt(this.reportId(), 10),
        reportFilters: this.appliedReportGroupFilters,
      };

      // runReportGQL stream
      this.apollo
        .query<RunReportAsyncQuery>({
          query: this.runReportAsyncGQL.document,
          variables: reportDataVariables,
          fetchPolicy: 'no-cache',
        })
        .subscribe({
          next: ({ data }) => {
            this.respond_to_switch_unit =
              !!data?.runReportAsync?.respond_to_switch_unit;
            // Start polling after initial delay
            timer(200)
              .pipe(takeUntilDestroyed(this._destroyRef))
              .subscribe(() => {
                if (!this.dataReceived) {
                  this.startPolling();
                }
              });
          },
          error: (error) => {
            this.handleError(error);
          },
        });
    } catch (error) {
      this.handleError(error);
    }
  }

  setChartOptions(reportConfigSubset: ReportConfig): void {
    try {
      const kpiCustomConfig: kpiCustomConfig =
        reportConfigSubset?.kpiCustomConfig;

      // 1. set title, subtitle, and description
      this.title = kpiCustomConfig?.title;
      this.subtitle = kpiCustomConfig?.subtitle;
      this.description = kpiCustomConfig?.description;
      if (!this.title) {
        this.handleError(new Error('Set title in kpiCustomConfig'));
      }

      // 2. set KPI main metric value
      const metricColumnName = kpiCustomConfig?.metricColumnName;
      if (!metricColumnName) {
        this.handleError(new Error('Set metricColumnName in kpiCustomConfig'));
      }
      let value: number;

      if (this.reportData.length > 0) {
        value = parseFloat(this.reportData[0][metricColumnName]);
        // check for non-numeric values
        if (typeof value === 'string') {
          this.valueToDisplay = this.reportData[0][metricColumnName];
          this.isLoading = false;
          return;
        }
        if (Number.isNaN(value)) {
          const error = new Error(
            'Try adjusting your filters to broaden the search.',
          );
          error.name = 'No data to display';
          this.handleError(error, 'communication');
        } else {
          this.valueToDisplay = this.formatNumber(
            value,
            kpiCustomConfig?.decimals,
            kpiCustomConfig?.prefix,
            kpiCustomConfig?.suffix,
            kpiCustomConfig?.isCompactFormat,
          );
        }
      } else {
        const error = new Error(
          'Try adjusting your filters to broaden the search.',
        );
        error.name = 'No data to display';
        this.handleError(error, 'communication');
      }

      // 3. (optional) set trend information
      const trendConfig = kpiCustomConfig?.trend;
      if (trendConfig?.enabled) {
        this.trendEnabled = true;
        const periodColumnName = trendConfig.periodColumnName;
        this.periodType = trendConfig.periodType || 'period';
        this.periodToDisplay = this.formatTimePeriod(
          this.reportData[0][periodColumnName],
          this.periodType,
        );

        // check there is a previous period to get trend info
        if (this.reportData.length > 1) {
          const previousValue = this.reportData[1][metricColumnName];
          // if periodType is provided in configs, add that in trend byline
          // eg " from previous year (1 000)"
          if (trendConfig.periodType) {
            this.previousValueToDisplay = ` from previous ${
              this.periodType
            } (${this.formatNumber(
              previousValue,
              kpiCustomConfig?.decimals,
              kpiCustomConfig?.prefix,
              kpiCustomConfig?.suffix,
              kpiCustomConfig?.isCompactFormat,
            )})`;
            // if periodType is not provided in configs, add previous time period (formatted) in trend byline
            // eg " from 1/1/2023 (1 000)"
          } else {
            this.periodType = this.formatTimePeriod(
              this.reportData[1][periodColumnName],
            );
            this.previousValueToDisplay = ` from ${
              this.periodType
            } (${this.formatNumber(
              previousValue,
              kpiCustomConfig?.decimals,
              kpiCustomConfig?.prefix,
              kpiCustomConfig?.suffix,
              kpiCustomConfig?.isCompactFormat,
            )})`;
          }
          this.percentChange = this.getPercentageChange(value, previousValue);
          this.percentChangeToDisplay = this.formatPercentChange();
        } else {
          this.previousValueToDisplay = `No data to compare previous ${this.periodType}`;
        }
      }
    } catch (error) {
      this.handleError(error);
    }

    this.isLoading = false;
  }

  // Utility function to parse JSON safely
  parseJSON(value: string, name: string) {
    try {
      return JSON.parse(value);
    } catch {
      const error = new Error(`Invalid JSON for ${name}`);
      error.name = 'Invalid JSON';
      this.handleError(error);
    }
  }

  // Extract report configurations
  getReportConfig(config: Config[]): ReportConfig {
    return config.reduce((obj, item) => {
      obj[item.name] = this.parseJSON(item.value, item.name);
      return obj;
    }, {});
  }

  getPercentageChange(value: number, previousValue: number): number {
    const percentChange = ((value - previousValue) / previousValue) * 100;
    return percentChange;
  }

  getPercentChangeColour(): string {
    if (this.percentChange < 0) {
      return 'text-red-500';
    } else if (this.percentChange > 0) {
      return 'text-emerald-400';
    } else {
      return '';
    }
  }

  formatTimePeriod(
    value: number | { '^t': number } | string,
    periodType?: string,
  ): string {
    const datePipeFormat =
      {
        year: 'yyyy',
        month: 'MMM yyyy',
        day: 'dd/MM/yyyy',
      }[periodType || 'day'] || 'dd/MM/yyyy';

    // If value is a number, return it as a string.
    if (typeof value === 'number') {
      return String(value);
    }

    // Eg: ?
    if (typeof value === 'object' && value !== null && '^t' in value) {
      return this.datePipe.transform(value['^t'] * 1000, datePipeFormat);
    }

    // Eg: 2024-01-01T00:00:00.000+00:00
    if (typeof value === 'string' && value.includes('T')) {
      return this.datePipe.transform(new Date(value), datePipeFormat);
    }

    // If the value is already a string but not an ISO date, return it as is.
    return value as string;
  }

  formatPercentChange(): string {
    let symbolPrefix = '';
    if (this.percentChange < 0) {
      symbolPrefix = '⬇';
    } else if (this.percentChange > 0) {
      symbolPrefix = '⬆';
    }

    return `${symbolPrefix} ${this.formatService.formatNumber(
      Math.abs(this.percentChange),
    )}%`;
  }

  formatNumber(
    value?: number,
    decimals?: number,
    prefix?: string,
    suffix?: string,
    isCompactFormat: boolean = false,
  ): string {
    let formattedValue: string;
    if (isCompactFormat) {
      formattedValue = this.formatService.formatNumberCompact(
        value,
        decimals,
        prefix,
        suffix,
      );
    } else {
      formattedValue = this.formatService.formatNumber(
        value,
        decimals,
        prefix,
        suffix,
      );
    }
    return formattedValue;
  }

  private startPolling(): void {
    this.pollingService.startPolling(
      // Poll function
      () => this.refetchReport(this.uuid),
      // Stop condition
      () => this.dataReceived,
      // Destroy subject
      this.destroy$,
    );
  }

  private initializeAutoRefresh(reportConfig: ReportConfig): void {
    const refreshRate = reportConfig?.kpiCustomConfig?.autoRefresh?.refreshRate;
    this.refreshRate = (refreshRate ? refreshRate : 300) * 1000; // set default refresh rate to 5 mins
    // Initiate autoRefresh mechanism
    this.refreshSubscription = this.autoRefreshService.setupAutoRefresh(
      this.refreshRate,
      this.destroy$,
      () => this.getChartAsync(),
    );
  }

  private subscribeToUnitChanges(): void {
    this.eventBus
      .getChannel('unitChange')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((unit) => Object.keys(unit).length > 0),
      ) // Ignore initial or invalid values)
      .subscribe((unit) => {
        if (unit) {
          this.onUnitChange(unit);
        }
      });
  }

  private onUnitChange(selectedUnit: Unit) {
    const reportConfigSubset = structuredClone(this.reportConfig);

    // Make substitutions if available for chart title, subtitle, and description
    const placeholder = '${unit}';
    const title: string = this.reportConfig?.kpiCustomConfig?.title.replace(
      placeholder,
      selectedUnit.label,
    );
    reportConfigSubset.kpiCustomConfig.title = title;

    // Also handle subtitle and description unit substitutions if they exist
    if (this.reportConfig?.kpiCustomConfig?.subtitle) {
      const subtitle: string =
        this.reportConfig.kpiCustomConfig.subtitle.replace(
          placeholder,
          selectedUnit.label,
        );
      reportConfigSubset.kpiCustomConfig.subtitle = subtitle;
    }

    if (this.reportConfig?.kpiCustomConfig?.description) {
      const description: string =
        this.reportConfig.kpiCustomConfig.description.replace(
          placeholder,
          selectedUnit.label,
        );
      reportConfigSubset.kpiCustomConfig.description = description;
    }

    // Keep only metricColumnName associated with the selected unit
    const selectedUnitValue = selectedUnit.value;
    if (reportConfigSubset?.kpiCustomConfig?.metricColumnName) {
      reportConfigSubset.kpiCustomConfig.metricColumnName = selectedUnitValue;
    }

    // trigger the refresh
    this.setChartOptions(reportConfigSubset);
  }
}
